package scan

import kotlin.js.Promise
import kotlin.js.json
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.khronos.webgl.Uint8ClampedArray
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.HTMLVideoElement
import puzzlekey.CENTRE_FILE
import puzzlekey.CentreFile
import puzzlekey.MODEL_FILE
import puzzlekey.Projector
import puzzlekey.RecentEmbeddings
import puzzlekey.Rgba
import puzzlekey.SCAN_FRAME
import puzzlekey.SealedBundle
import puzzlekey.WebScanModel
import puzzlekey.guideInVideo
import puzzlekey.guideOnScreen
import puzzlekey.modelInput

/** How often the site scans a frame (ADR-0011 §1: the site retries every frame). */
const val SCAN_INTERVAL_MS = 600

/** Milliseconds each step of one Scan took. */
class ScanTimings(val grab: Double, val input: Double, val model: Double, val project: Double) {
    val total get() = grab + input + model + project
}

/** One frame through the Scan pipeline: its [PROJECTIONS][puzzlekey.PROJECTIONS] projection values (sign = bit, |value| = confidence). */
class Scan(val projection: DoubleArray, val timings: ScanTimings)

/**
 * The Scan pipeline in the browser (ADR-0004, ADR-0009 §1): the video pixels under the guide overlay,
 * drawn at the corpus's guide size ([SCAN_FRAME]), then the same `commonMain` crop and preprocessing as
 * `seal`, the model, and the projections. [recent] averages embeddings over frames when switched on.
 */
class Scanner(private val model: WebScanModel, private val projector: Projector, var recent: RecentEmbeddings = RecentEmbeddings()) {
    private val canvas = (document.createElement("canvas") as HTMLCanvasElement).apply {
        width = SCAN_FRAME.w
        height = SCAN_FRAME.h
    }
    private val context = canvas.getContext("2d", json("willReadFrequently" to true)) as CanvasRenderingContext2D

    /** Scans the current frame of [video], which shows the camera with `object-fit: cover` under the guide overlay. */
    suspend fun scan(video: HTMLVideoElement): Scan {
        check(video.readyState >= 2 && video.videoWidth > 0) { "the camera has no frame yet" }
        val t0 = now()
        val view = video.clientWidth.toDouble() to video.clientHeight.toDouble()
        val guide = guideInVideo(view.first, view.second, video.videoWidth, video.videoHeight, guideOnScreen(view.first, view.second))
        context.drawImage(
            video,
            guide.x.toDouble(), guide.y.toDouble(), guide.w.toDouble(), guide.h.toDouble(),
            0.0, 0.0, SCAN_FRAME.w.toDouble(), SCAN_FRAME.h.toDouble(),
        )
        val frame = Rgba(SCAN_FRAME.w, SCAN_FRAME.h, bytesOf(context.getImageData(0.0, 0.0, SCAN_FRAME.w.toDouble(), SCAN_FRAME.h.toDouble()).data))
        val t1 = now()
        val input = modelInput(frame, SCAN_FRAME)
        val t2 = now()
        val embedding = model.embed(input)
        val t3 = now()
        val projection = projector.project(recent.next(embedding))
        val t4 = now()
        return Scan(projection, ScanTimings(t1 - t0, t2 - t1, t3 - t2, t4 - t3))
    }
}

/** How much of the Scan model's download has arrived; [total] is null when the server doesn't say. */
class Download(val received: Double, val total: Double?) {
    /** 0…1, short of 1 until the last byte; null when the total is unknown. */
    val fraction: Double? get() = total?.let { (received / it).coerceAtMost(0.99) }
}

/** ONNX Runtime Web's WASM binary, which it would otherwise fetch itself without progress. */
private const val ORT_WASM = "ort/ort-wasm-simd-threaded.wasm"

/**
 * Loads the vendored ONNX Runtime Web and the Scan model from the deploy (`ort/`, `model/`; see
 * site/build.gradle.kts). Slow on a phone: about 20 MB (16 s over mobile data, T12), fetched once and
 * then cached, so the two big files are fetched here and [onProgress] follows them.
 */
suspend fun loadScanModel(onProgress: (Download) -> Unit = {}): WebScanModel {
    loadScript("ort/ort.wasm.min.js")
    val ort: dynamic = window.asDynamic().ort
    // An absolute prefix: ORT imports its WASM glue relative to its own script otherwise.
    ort.env.wasm.wasmPaths = js("new URL('ort/', document.baseURI).href")
    val (wasm, model) = fetchBytes(listOf(ORT_WASM, "model/$MODEL_FILE"), onProgress)
    ort.env.wasm.wasmBinary = wasm
    return WebScanModel.load(ort, model)
}

/** [paths] fetched together, as `Uint8Array`s, reporting the bytes so far across all of them. */
private suspend fun fetchBytes(paths: List<String>, onProgress: (Download) -> Unit): List<dynamic> {
    val responses = paths.map { window.fetch(it) }.mapIndexed { i, pending ->
        pending.await().also { check(it.ok) { "${paths[i]}: HTTP ${it.status}" } }
    }
    // A compressed response's Content-Length counts the compressed bytes, but the stream gives the others.
    val lengths = responses.map { r ->
        r.headers.get("Content-Length")?.toDoubleOrNull()?.takeIf { r.headers.get("Content-Encoding").let { it == null || it == "identity" } }
    }
    val total = if (lengths.all { it != null }) lengths.sumOf { it!! } else null
    var received = 0.0
    onProgress(Download(received, total))
    return responses.map { response ->
        val reader: dynamic = response.asDynamic().body.getReader()
        val chunks = mutableListOf<dynamic>()
        var size = 0
        while (true) {
            val read: dynamic = (reader.read() as Promise<dynamic>).await()
            if (read.done == true) break
            chunks.add(read.value)
            size += read.value.length as Int
            received += read.value.length as Int
            onProgress(Download(received, total))
        }
        concat(chunks, size)
    }
}

@Suppress("UNUSED_PARAMETER")
private fun concat(chunks: List<dynamic>, size: Int): dynamic {
    val all: dynamic = js("new Uint8Array(size)")
    var offset = 0
    for (chunk in chunks) {
        all.set(chunk, offset)
        offset += chunk.length as Int
    }
    return all
}

/**
 * The public centre file shipped with the model: the debug view projects with it, and check mode compares
 * its model id with `sealed.json`'s, whose own centre and seed it projects with.
 */
suspend fun loadCentre(): CentreFile = CentreFile.parse(fetchText("model/$CENTRE_FILE"))

/** `sealed.json` from the deploy, or null when it is unreadable by this code ([SealedBundle.fromJson]). */
suspend fun loadSealed(): SealedBundle? = SealedBundle.fromJson(fetchText("sealed.json"))

private suspend fun fetchText(path: String): String {
    val response = window.fetch(path).await()
    check(response.ok) { "$path: HTTP ${response.status}" }
    return response.text().await()
}

private suspend fun loadScript(src: String) {
    val script = document.createElement("script") as HTMLScriptElement
    Promise { resolve, reject ->
        script.onload = { resolve(Unit) }
        script.onerror = { _, _, _, _, _ -> reject(IllegalStateException("couldn't load $src")) }
        script.src = src
        document.head!!.appendChild(script)
    }.await()
}

private fun now(): Double = window.performance.now()

/** The canvas pixels as the [Rgba] layout `commonMain` reads, without copying: an Int8Array over the same buffer. */
@Suppress("UNUSED_PARAMETER")
private fun bytesOf(data: Uint8ClampedArray): ByteArray = js("new Int8Array(data.buffer, data.byteOffset, data.length)")
